import { Tooltip } from "@adasupport/byron";
import { isBefore } from "date-fns";
// Moment is no longer supported. Slack channel #p-remove-moment for details
// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import moment from "moment";
import React, { useEffect, useState } from "react";

import { Button } from "components/Common/Button";
import { TODAY_LOCAL } from "services/date-strings";

import {
  CURRENT_MONTH,
  DATE_FORMAT,
  DAY_NAMES,
  NEXT_MONTH,
  PREV_MONTH,
} from "./constants";
import * as S from "./styles";

interface Props {
  onChange: (startDate: string) => void;
  startDate: string;
  endDate?: string | undefined;
  maxDate?: string | undefined;
  minDate?: string | undefined;
  minDateTooltip?: string;
}

export const DoubleDatePicker = ({
  onChange,
  startDate,
  endDate,
  maxDate,
  minDate,
  minDateTooltip,
}: Props) => {
  const [visibleMonth, setVisibleMonth] = useState<string>(
    moment.utc(TODAY_LOCAL).toISOString(),
  );

  useEffect(() => {
    const newMonth = moment.utc(endDate || startDate);
    const currentMonth = moment.utc(visibleMonth);

    if (
      newMonth.isValid() &&
      !newMonth.isSame(currentMonth, "month") &&
      !newMonth.isSame(currentMonth.subtract(1, "months"), "month")
    ) {
      setVisibleMonth(newMonth.toISOString());
    }
  }, [startDate, endDate]);

  const handleChangeMonth = (increment: number) => () => {
    const newMonth = moment.utc(visibleMonth).add(increment, "months");
    setVisibleMonth(newMonth.toISOString());
  };

  const handleDateClick = (isDisabled: boolean, date: string) => () => {
    if (!isDisabled) {
      onChange(date);
    }
  };

  const renderCalendar = (monthToRender: number): React.ReactNode => {
    const monthObj = moment.utc(visibleMonth).add(monthToRender, "months");

    const startPadding = [...Array(monthObj.date(1).day())];
    const daysInMonth = [...Array(monthObj.daysInMonth())];

    return (
      <S.CalendarGrid>
        {DAY_NAMES.map((day, index) => (
          <S.DayLabel key={index}>{day}</S.DayLabel>
        ))}
        {startPadding.map((o, i) => (
          <div key={`${monthObj.month() + i}`} />
        ))}
        {daysInMonth.map((o, i) => {
          const currentDate = monthObj.date(i + 1).startOf("day");

          const isToday = currentDate.isSame(moment.utc(TODAY_LOCAL), "day");

          const isStartDate = currentDate.isSame(moment.utc(startDate), "day");
          const isEndDate = currentDate.isSame(moment.utc(endDate), "day");
          const isInRange = currentDate.isBetween(startDate, endDate);

          const isDisabled = Boolean(
            (maxDate && moment.utc(maxDate).isBefore(currentDate)) ||
              (minDate && moment.utc(minDate).isAfter(currentDate)),
          );

          let v = (
            <S.DateButtonText isToday={isToday}>{i + 1}</S.DateButtonText>
          );

          if (
            minDate &&
            minDateTooltip &&
            isBefore(
              new Date(currentDate.format(DATE_FORMAT)),
              new Date(minDate),
            )
          ) {
            v = <Tooltip text={minDateTooltip}>{v}</Tooltip>;
          }

          return (
            <S.DateButton
              key={currentDate.date()}
              isStartDate={isStartDate}
              isEndDate={isEndDate}
              isInRange={isInRange}
              isDisabled={isDisabled}
              onClick={handleDateClick(
                isDisabled,
                currentDate.format(DATE_FORMAT),
              )}
            >
              {v}
            </S.DateButton>
          );
        })}
      </S.CalendarGrid>
    );
  };

  const showPrevButton =
    !minDate || moment.utc(visibleMonth).isSameOrAfter(minDate);
  const showNextButton =
    !maxDate || moment.utc(visibleMonth).isSameOrBefore(maxDate);

  return (
    <S.Root>
      <S.MonthName>
        {moment.utc(visibleMonth).subtract(1, "months").format("MMMM YYYY")}
        {showPrevButton && (
          <S.PrevButton>
            <Button
              onClick={handleChangeMonth(PREV_MONTH)}
              icon="ChevronLeft"
              light
              clear
            />
          </S.PrevButton>
        )}
      </S.MonthName>
      <S.MonthName>
        {moment.utc(visibleMonth).format("MMMM YYYY")}
        {showNextButton && (
          <S.NextButton>
            <Button
              onClick={handleChangeMonth(NEXT_MONTH)}
              icon="ChevronRight"
              light
              clear
            />
          </S.NextButton>
        )}
      </S.MonthName>

      {renderCalendar(PREV_MONTH)}
      {renderCalendar(CURRENT_MONTH)}
    </S.Root>
  );
};
